import React, { useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useChild } from '../contexts/ChildContext';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import Sidebar from './Sidebar';
import '../styles/Layout.css';

const DOUBLE_CLICK_MS = 300;

/** 只有一個兒童時，給家長看的操作提示 */
const BUBBLE_PARENT_KEYS = ['bubble.parentTip1', 'bubble.parentTip2', 'bubble.parentTip3', 'bubble.parentTip4', 'bubble.parentTip5'] as const;

/** 是否為課程詳情頁（含 childId/courseId），此頁不允許連點切換兒童，因各兒童課程不同無法即時刷新 */
const isCourseDetailPage = (pathname: string) => /^\/courses\/[^/]+\/[^/]+$/.test(pathname);

const MainLayout: React.FC = () => {
    const { currentUser } = useAuth();
    const { t } = useI18n();
    const { pathname } = useLocation();
    const { children, selectedChild, setSelectedChildId, refreshChildren } = useChild();
    const clickCountRef = useRef(0);
    const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [showBubble, setShowBubble] = useState(false);
    const [bubbleMessage, setBubbleMessage] = useState('');

    const handleChildAvatarClick = async () => {
        if (!currentUser) return;

        let latestChildren = children;
        try {
            latestChildren = await refreshChildren();
        } catch {
            // 刷新失敗時使用 context 快取
        }

        // 課程詳情頁不允許切換兒童（各兒童課程不同，無法即時刷新）
        if (isCourseDetailPage(pathname)) {
            const msg = t(BUBBLE_PARENT_KEYS[Math.floor(Math.random() * BUBBLE_PARENT_KEYS.length)]);
            setBubbleMessage(msg);
            setShowBubble(true);
            setTimeout(() => setShowBubble(false), 8000);
            return;
        }

        // 超過一個兒童：顯示「連點可以切換」，雙擊切換
        if (latestChildren.length > 1 && selectedChild) {
            clickCountRef.current += 1;
            if (clickTimerRef.current) clearTimeout(clickTimerRef.current);

            if (clickCountRef.current === 2) {
                clickCountRef.current = 0;
                const idx = latestChildren.findIndex(c => c.id === selectedChild.id);
                const next = latestChildren[(idx + 1) % latestChildren.length];
                setSelectedChildId(next.id);
                setShowBubble(false);
            } else {
                setBubbleMessage(t('bubble.doubleTapToSwitch'));
                setShowBubble(true);
                setTimeout(() => setShowBubble(false), 8000);
                clickTimerRef.current = setTimeout(() => {
                    clickCountRef.current = 0;
                }, DOUBLE_CLICK_MS);
            }
        } else {
            /* 單一兒童：顯示給家長看的操作提示 */
            const msg = t(BUBBLE_PARENT_KEYS[Math.floor(Math.random() * BUBBLE_PARENT_KEYS.length)]);
            setBubbleMessage(msg);
            setShowBubble(true);
            setTimeout(() => setShowBubble(false), 8000);
        }
    };

    const renderAvatar = () => {
        if (!selectedChild) return null;
        const isEmoji = /^[\u{1F300}-\u{1F9FF}]|[\u2600-\u26FF]|[\u2700-\u27BF]/u.test(selectedChild.avatar) || selectedChild.avatar.length <= 2;
        if (isEmoji) {
            return (
                <div
                    className="child-avatar-emoji"
                    style={{
                        background: (selectedChild.color || '#6B8EDB') + '25',
                        border: `3px solid ${selectedChild.color || '#6B8EDB'}`,
                    }}
                >
                    {selectedChild.avatar}
                </div>
            );
        }
        return (
            <img
                src={selectedChild.avatar}
                alt={selectedChild.name}
                className="child-avatar-image"
            />
        );
    };

    return (
        <div className="layout">
            <Sidebar />
            <main className="main-content">
                <Outlet />
            </main>

            {/* 右下角兒童貼圖，雙擊切換 */}
            {selectedChild && (
                <div
                    className="child-avatar-container"
                    onClick={handleChildAvatarClick}
                >
                    {showBubble && bubbleMessage && (
                        <div className="child-avatar-bubble">
                            {bubbleMessage}
                        </div>
                    )}
                    {renderAvatar()}
                </div>
            )}
        </div>
    );
};

export default MainLayout;
